/* global bootstrap */
/* global HSCore */

import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import axiosInstance from "../../utils/Requests/axios";
import UserContext from "../../utils/Contexts/UserContext";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import EditIcon from "../svg/Edit";
import DeleteIcon from "../svg/Delete";
import Constant from "../Constants";

import "./style.css"

function WebTable({
  data,
  setData,
  fetchData,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) {
  const { t, language } = useContext(LanguageContext);
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const { categoryOptions } = Constant();
  const [isLoading, setIsLoading] = useState(true);
  const [isTomSelectInit, setIsTomSelectInit] = useState(false);
  const setColumnSort = (columnIndex) => {
    const columnSortData = { ...columnSorts };
    if (!columnSortData[columnIndex]) columnSortData[columnIndex] = "asc";
    else if (columnSortData[columnIndex] === "asc")
      columnSortData[columnIndex] = "desc";
    else if (columnSortData[columnIndex] === "desc")
      columnSortData[columnIndex] = null;
    setColumnSorts(columnSortData);
  };

  const navigateToOffer = (item) => {
    const hasOffers = item?.offers?.length > 0;
    const url = `/websites/${item?.domain?.uuid}${
      hasOffers ? "#offersSection" : ""
    }`;
    navigate(url);

    if (hasOffers) {
      setTimeout(() => {
        const offersSection = document.getElementById("offersSection");
        if (offersSection) {
          const headerOffset =
            document.getElementById("header")?.offsetHeight || 80;
          const sectionPosition =
            offersSection.getBoundingClientRect().top + window.scrollY;
          const offsetPosition = sectionPosition - headerOffset - 20;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 1200);
    }
  };

  const navigateToEditWeb = (item) => {
    navigate(`/websites/${item?.uuid}`);
  };

  let isSelectVisible = false;
  const previousPayloadRef = useRef(null);
  useEffect(() => {
    setIsTomSelectInit(false);

    const payload = { ...data };
    const currentDomainItems = _.sortBy(
      payload,
      (item) => item?.domain?.domain
    ).map((item) => item?.domain?.uuid);
    const previousDomainItems = _.sortBy(
      previousPayloadRef.current,
      (item) => item?.domain?.domain
    ).map((item) => item?.domain?.uuid);

    if (!_.isEqual(previousDomainItems, currentDomainItems)) {
      previousPayloadRef.current = payload;

      // Initialize Tom Select and handle visibility
      const selectElements = document.querySelectorAll(".js-select-tab");
      selectElements.forEach((selectElement) => {
        // Initially hide the select element
        selectElement.style.display = "none";

        // Initialize Tom Select
        HSCore.components.HSTomSelect.init(selectElement);

        // After initialization, make the select element visible
        requestAnimationFrame(() => {
          selectElement.style.display = "block"; // Make the select visible after initialization
        });

        // Handle selected count text
        const selectedCountElement =
          selectElement.parentElement.querySelector(".ts-selected-count");
        if (selectedCountElement) {
          const count =
            selectedCountElement.textContent.match(/\d+/)?.[0] || "";
          selectedCountElement.textContent = `${count} ${t(
            "item(s) selected"
          )}`;
        }
      });

      // Initialize Bootstrap tooltips
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      [...tooltipTriggerList].forEach(
        (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
      );

      setIsTomSelectInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (isTomSelectInit) {
      requestAnimationFrame(() => setIsLoading(false));
    }
  }, [isTomSelectInit]);

  const previousLanguageRef = useRef(language);
  useEffect(() => {
    if (!_.isEqual(previousLanguageRef.current, language)) {
      setData(null);
      fetchData();
      previousLanguageRef.current = language;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const previousVisibleRef = useRef(columnsVisibility);
  useEffect(() => {
    if (!_.isEqual(previousVisibleRef.current, columnsVisibility)) {
      setData(null);
      fetchData();
      previousVisibleRef.current = columnsVisibility;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsVisibility]);

  const handleTermsChange = (uuid, updatedFields) => {
    const url = `sellers/${userData?.uuid}/terms/${uuid}`;
    const payload = {
      money_back_policy: updatedFields.money_back_policy || "",
      link_replacement_policy: updatedFields.link_replacement_policy || "",
      words: updatedFields.words || "",
      words_limit: updatedFields.words_limit || "",
      image: updatedFields.image || false,
      plagiarism: updatedFields.plagiarism || false,
      seo: updatedFields.seo || false,
      readability: updatedFields.readability || false,
      ai: updatedFields.ai || false,
    };

    axiosInstance
      .put(url, payload)
      .then((response) => {
        const clonedData = _.cloneDeep(data);
        const webItem = clonedData.find((item) => item?.terms?.uuid === uuid);
        const responseData = response?.data;
        webItem.terms.link_replacement_policy =
          responseData?.link_replacement_policy;
        webItem.terms.money_back_policy = responseData?.money_back_policy;
        webItem.terms.words = responseData?.words;
        webItem.terms.words_limit = responseData?.words_limit;
        webItem.terms.image = responseData?.image;
        webItem.terms.plagiarism = responseData?.plagiarism;
        webItem.terms.seo = responseData?.seo;
        webItem.terms.readability = responseData?.readability;
        webItem.terms.ai = responseData?.ai;
        setData(clonedData);
      })
      .catch(() => {
        toast.error(t("There was an error updating terms."));
      });
  };

  const [minMaxValue, setMinMaxValue] = useState("-");
  const [isValid, setIsValid] = useState(true);
  const [editStates, setEditStates] = useState({});

  const handleMinMaxChange = (e) => {
    let value = e.target.value;
    if (value && !value.includes("-")) {
      value = `${value}-`;
    }
    setMinMaxValue(value);
    const [min, max] = value.split("-").map((v) => v.trim());
    if (min) {
      if (max && parseInt(max) > 0 && parseInt(min) > parseInt(max)) {
        setIsValid(false);
        toast.error(t("Min value should be less than max value."));
      } else {
        setIsValid(true);
      }
    } else {
      setIsValid(false);
    }
  };

  useEffect(() => {
    const initialStates = data?.reduce((acc, item) => {
      acc[item?.terms?.uuid] = false;
      return acc;
    }, {});
    setEditStates(initialStates);
  }, [data]);

  const handleEditClick = (uuid, currentWords, currentWordsLimit) => {
    setEditStates((prevState) => ({
      ...prevState,
      [uuid]: !prevState[uuid],
    }));
    setMinMaxValue(
      `${_.isNull(currentWords) ? "" : currentWords}-${
        _.isNull(currentWordsLimit) ? "" : currentWordsLimit
      }`
    );
  };

  const handleBlur = (item) => {
    const [min, max] = minMaxValue.split("-").map((value) => value.trim());

    if (
      (_.isNull(item?.terms?.words) ? min : parseInt(min)) ===
        (_.isNull(item?.terms?.words) ? "" : item?.terms?.words) &&
      (_.isNull(item?.terms?.words_limit) ? max : parseInt(max)) ===
        (_.isNull(item?.terms?.words_limit) ? "" : item?.terms?.words_limit)
    ) {
      if (isValid)
        setEditStates((prevState) => ({
          ...prevState,
          [item?.terms?.uuid]: false,
        }));
      return;
    }

    if (min) {
      if (isValid) {
        const updatedTerms = {
          ...item?.terms,
          words: min,
          words_limit: max || null,
        };
        handleTermsChange(item?.terms?.uuid, updatedTerms);
        setIsValid(true); // Set as valid if both values are present
        setEditStates((prevState) => ({
          ...prevState,
          [item?.terms?.uuid]: false,
        }));
      } else {
        setIsValid(false);
        toast.error(t("Add valid min and max value."));
      }
    }
  };

  const handleShowHideOffer = (termsUuid, val) => {
    const url = `sellers/${userData?.uuid}/terms/${termsUuid}/${val}`;
    axiosInstance
      .put(url)
      .then((response) => {
        const clonedData = _.cloneDeep(data);
        const webItem = clonedData.find(
          (item) => item?.terms?.uuid === termsUuid
        );
        const responseData = response?.data;
        webItem.terms.hidden = responseData?.hidden;
        setData(clonedData);
      })
      .catch(() => {
        toast.error(t("There was an error updating offer visibility."));
      });
  };

  const handleDeleteWeb = (termsUuid) => {
    setData(null);
    const url = `sellers/${userData?.uuid}/terms/${termsUuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        const clonedData = _.cloneDeep(data);
        const filteredWebItems = clonedData.filter(
          (item) => item?.terms?.uuid !== termsUuid
        );
        setData(filteredWebItems);
      })
      .catch(() => {
        toast.error(t("There was an error deleting Website."));
      });
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        id="datatable"
        className="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table"
      >
        <thead className="thead-light">
          <tr>
            {columnsVisibility.domain && (
              <th
                style={{ fontSize: "14px", fontWeight: "600" }}
                className={`table-column-ps-2 col-11 sorting${
                  !columnSorts[0]
                    ? ""
                    : columnSorts[0] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(0)}
              >
                {t("Domains")}
              </th>
            )}
            {columnsVisibility.status && (
              <th
                style={{ fontSize: "14px", fontWeight: "600" }}
                className={`sorting${
                  !columnSorts[1]
                    ? ""
                    : columnSorts[1] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(1)}
              >
                {t("Status")}
              </th>
            )}
            {columnsVisibility.affiliation && (
              <th
                style={{ fontSize: "14px", fontWeight: "600" }}
                className={`sorting${
                  !columnSorts[2]
                    ? ""
                    : columnSorts[2] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(2)}
              >
                {t("AFFILIATION")}
              </th>
            )}
            {columnsVisibility.money_back && (
              <th
                style={{ fontSize: "14px", fontWeight: "600" }}
                className={`sorting${
                  !columnSorts[3]
                    ? ""
                    : columnSorts[3] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(3)}
              >
                {t("Money Back")}
              </th>
            )}

            {columnsVisibility.link_replacement && (
              <th
                style={{ fontSize: "14px", fontWeight: "600" }}
                className={`sorting${
                  !columnSorts[4]
                    ? ""
                    : columnSorts[4] === "asc"
                    ? "_asc"
                    : "_desc"
                }`}
                onClick={() => setColumnSort(4)}
              >
                {t("Link Replacement")}
              </th>
            )}
            {columnsVisibility.words && (
              <th style={{ fontSize: "14px", fontWeight: "600" }}>
                {t("WORDS")}
              </th>
            )}
            {columnsVisibility.checks && (
              <th style={{ fontSize: "14px", fontWeight: "600" }}>
                {t("CHECKS")}
              </th>
            )}
            {columnsVisibility.offers && (
              <th style={{ fontSize: "14px", fontWeight: "600" }}>
                {t("OFFERS")}
              </th>
            )}
            <th className="table-column-ps-0"></th>
          </tr>
        </thead>

        <tbody id="nav-one-eg1" aria-labelledby="nav-one-eg1-tab">
          {!!data?.length &&
            data?.map((item, index) => (
              <tr
                key={index}
                style={{ visibility: isLoading ? "collapse" : "visible" }}
              >
                {columnsVisibility.domain && (
                  <td className="table-column-ps-2">
                    <div className="d-flex align-items-center mb-2">
                      <div className="flex-grow-1">
                        <span
                          className={`responsive-text-web-table cursor-pointer ${
                            item?.terms?.hidden === false
                              ? "text-inherit"
                              : "text-muted"
                          }`}
                          onClick={() => navigateToEditWeb(item?.domain)}
                        >
                          {item?.domain?.domain}
                        </span>
                        {item?.terms?.hidden !== false && (
                          <i
                            className="bi-eye-slash text-muted ms-1"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Not Visible in the Public Offers"
                          ></i>
                        )}
                      </div>
                    </div>
                    {item?.domain?.categories?.length ? (
                      <span>
                        {item?.domain?.categories
                          ?.slice(0, 3)
                          ?.map((category, i) => (
                            <span
                              key={i}
                              className="badge bg-soft-secondary text-secondary me-1"
                              style={{ fontSize: "12px", fontWeight: "600" }}
                            >
                              {categoryOptions[category]}
                            </span>
                          ))}
                        {item?.domain?.categories?.length > 3 ? (
                          <span className="badge bg-soft-secondary text-secondary">
                            ...
                          </span>
                        ) : (
                          ""
                        )}
                      </span>
                    ) : (
                      <span
                        className="badge bg-soft-secondary text-secondary"
                        style={{ fontSize: "12px", fontWeight: "600" }}
                      >
                        Any Category
                      </span>
                    )}
                  </td>
                )}
                {columnsVisibility.status && (
                  <td>
                    <span
                      className={`badge-website badge ${
                        item?.domain?.domain_status === 0
                          ? "bg-softer-warning"
                          : item?.domain?.domain_status === 1
                          ? "bg-soft-danger text-danger"
                          : item?.domain?.domain_status === 2
                          ? "bg-soft-success text-success"
                          : ""
                      }`}
                      style={{
                        color:
                          item?.domain?.domain_status === 0 ? "#FAC710" : "",
                      }}
                    >
                      {item?.domain?.domain_status === 0
                        ? t("Pending Review")
                        : item?.domain?.domain_status === 1
                        ? t("Declined")
                        : item?.domain?.domain_status === 2
                        ? t("Approved")
                        : ""}
                      {item?.domain?.domain_status === 1 && (
                        <i
                          className="bi bi-question-circle ms-2"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-original-title="Declined due to Terms of Service section 4.1.4"
                        ></i>
                      )}
                    </span>
                  </td>
                )}
                {columnsVisibility.affiliation && (
                  <td>
                    <span
                      className={`badge badge-affiliation ${
                        item?.terms?.affiliation === 0
                          ? "bg-soft-secondary text-secondary"
                          : item?.terms?.affiliation === 2
                          ? "bg-soft-primary text-primary"
                          : item?.terms?.affiliation === 1
                          ? "bg-soft-success text-success"
                          : ""
                      }`}
                    >
                      {item?.terms?.affiliation === 0
                        ? t("Not Verified")
                        : item?.terms?.affiliation === 1
                        ? t("Owner")
                        : item?.terms?.affiliation === 2
                        ? t("Reseller")
                        : ""}
                    </span>
                  </td>
                )}
                {columnsVisibility.money_back && (
                  <td>
                    <div className="tom-select-custom">
                      <select
                        className="js-money-back js-select-tab form-select"
                        autoComplete="off"
                        data-hs-tom-select-options='{
                          "placeholder": "Select Moneyback Policy",
                          "hideSearch": true,
                          "dropdownParent": "body"
                        }'
                        onChange={(e) => {
                          const updatedTerms = {
                            ...item?.terms,
                            money_back_policy: e.target.value,
                          };
                          handleTermsChange(item?.terms?.uuid, updatedTerms);
                        }}
                      >
                        <option
                          value={0}
                          selected={item?.terms?.money_back_policy === 0}
                        >
                          {t("Not Provided")}
                        </option>
                        <option
                          value={1}
                          selected={item?.terms?.money_back_policy === 1}
                        >
                          5 {t("days")}
                        </option>
                        <option
                          value={2}
                          selected={item?.terms?.money_back_policy === 2}
                        >
                          10 {t("days")}
                        </option>
                        <option
                          value={3}
                          selected={item?.terms?.money_back_policy === 3}
                        >
                          15 {t("days")}
                        </option>
                        <option
                          value={4}
                          selected={item?.terms?.money_back_policy === 4}
                        >
                          20 {t("days")}
                        </option>
                        <option
                          value={5}
                          selected={item?.terms?.money_back_policy === 5}
                        >
                          25 {t("days")}
                        </option>
                        <option
                          value={6}
                          selected={item?.terms?.money_back_policy === 6}
                        >
                          30 {t("days")}
                        </option>
                        <option
                          value={7}
                          selected={item?.terms?.money_back_policy === 7}
                        >
                          40 {t("days")}
                        </option>
                        <option
                          value={8}
                          selected={item?.terms?.money_back_policy === 8}
                        >
                          50 {t("days")}
                        </option>
                        <option
                          value={9}
                          selected={item?.terms?.money_back_policy === 9}
                        >
                          60 {t("days")}
                        </option>
                        <option
                          value={10}
                          selected={item?.terms?.money_back_policy === 10}
                        >
                          70 {t("days")}
                        </option>
                        <option
                          value={11}
                          selected={item?.terms?.money_back_policy === 11}
                        >
                          80 {t("days")}
                        </option>
                        <option
                          value={12}
                          selected={item?.terms?.money_back_policy === 12}
                        >
                          90 {t("days")}
                        </option>
                      </select>
                    </div>
                  </td>
                )}
                {columnsVisibility.link_replacement && (
                  <td>
                    <div className="tom-select-custom">
                      <select
                        className="js-select-tab form-select js-months"
                        autoComplete="off"
                        data-hs-tom-select-options='{
                          "placeholder": "Select Link Replacement Policy",
                          "hideSearch": true,
                          "dropdownParent": "body"
                        }'
                        onChange={(e) => {
                          const updatedTerms = {
                            ...item?.terms,
                            link_replacement_policy: e.target.value,
                          };
                          handleTermsChange(item?.terms?.uuid, updatedTerms);
                        }}
                      >
                        <option
                          value={0}
                          selected={item?.terms?.money_back_policy === 0}
                        >
                          {t("Not Provided")}
                        </option>
                        <option
                          value={1}
                          selected={item?.terms?.link_replacement_policy === 1}
                        >
                          1 {t("month")}
                        </option>
                        <option
                          value={2}
                          selected={item?.terms?.link_replacement_policy === 2}
                        >
                          2 {t("months")}
                        </option>
                        <option
                          value={3}
                          selected={item?.terms?.link_replacement_policy === 3}
                        >
                          3 {t("months")}
                        </option>
                        <option
                          value={4}
                          selected={item?.terms?.link_replacement_policy === 4}
                        >
                          4 {t("months")}
                        </option>
                        <option
                          value={5}
                          selected={item?.terms?.link_replacement_policy === 5}
                        >
                          5 {t("months")}
                        </option>
                        <option
                          value={6}
                          selected={item?.terms?.link_replacement_policy === 6}
                        >
                          6 {t("months")}
                        </option>
                        <option
                          value={7}
                          selected={item?.terms?.link_replacement_policy === 7}
                        >
                          7 {t("months")}
                        </option>
                        <option
                          value={8}
                          selected={item?.terms?.link_replacement_policy === 8}
                        >
                          8 {t("months")}
                        </option>
                        <option
                          value={9}
                          selected={item?.terms?.link_replacement_policy === 9}
                        >
                          9 {t("months")}
                        </option>
                        <option
                          value={10}
                          selected={item?.terms?.link_replacement_policy === 10}
                        >
                          10 {t("months")}
                        </option>
                        <option
                          value={11}
                          selected={item?.terms?.link_replacement_policy === 11}
                        >
                          11 {t("months")}
                        </option>
                        <option
                          value={12}
                          selected={item?.terms?.link_replacement_policy === 12}
                        >
                          12 {t("months")}
                        </option>
                      </select>
                    </div>
                  </td>
                )}
                {columnsVisibility.words && (
                  <td>
                    {editStates?.[item?.terms?.uuid] === false ? (
                      <button
                        className="btn btn-white btn-sm text-start web-table-input"
                        type="button"
                        onClick={() =>
                          handleEditClick(
                            item?.terms?.uuid,
                            item?.terms?.words,
                            item?.terms?.words_limit
                          )
                        }
                      >
                        <i style={{ marginRight: "8px" }}>
                          <EditIcon />
                        </i>
                        {item?.terms?.words === null &&
                        !item?.terms?.words_limit === null
                          ? "min - max"
                          : `${
                              item?.terms?.words === null
                                ? "min"
                                : item?.terms?.words.toLocaleString()
                            }
                            ${item?.terms?.words_limit === null ? "" : " - "}
                            ${
                              item?.terms?.words_limit === null
                                ? ""
                                : item?.terms?.words_limit.toLocaleString()
                            }`}
                      </button>
                    ) : (
                      <input
                        autoFocus
                        type="text"
                        className="form-control form-control-sm web-table-input"
                        value={minMaxValue}
                        placeholder="min - max"
                        onChange={handleMinMaxChange}
                        onBlur={() => handleBlur(item)}
                        style={{
                          borderColor: isValid ? "" : "#BA0021",
                        }}
                      />
                    )}
                  </td>
                )}
                {columnsVisibility.checks && (
                  <td>
                    <div className="tom-select-custom">
                      <select
                        className="js-select-tab form-select js-checks"
                        autoComplete="off"
                        multiple
                        style={{ display: isSelectVisible ? "block" : "none" }}
                        data-hs-tom-select-options='{
                          "singleMultiple": true,
                          "hideSelected": false,
                          "hideSearch": true,
                          "placeholder": "Select Terms",
                          "dropdownParent": "body"
                        }'
                        onChange={(e) => {
                          const selectedTerms = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value
                          );
                          const updatedTerms = {
                            ...item?.terms,
                            image: selectedTerms.includes("Image"),
                            plagiarism: selectedTerms.includes("Plagiarism"),
                            seo: selectedTerms.includes("SEO"),
                            readability: selectedTerms.includes("Readability"),
                            ai: selectedTerms.includes("AI Allowed"),
                          };
                          handleTermsChange(item?.terms?.uuid, updatedTerms);
                        }}
                      >
                        <option value="Image" selected={item?.terms?.image}>
                          {t("Image")}
                        </option>
                        <option
                          value="Plagiarism"
                          selected={item?.terms?.plagiarism}
                        >
                          {t("Plagiarism")}
                        </option>
                        <option value="SEO" selected={item?.terms?.seo}>
                          {t("SEO")}
                        </option>
                        <option
                          value="Readability"
                          selected={item?.terms?.readability}
                        >
                          {t("Readability")}
                        </option>
                        <option value="AI Allowed" selected={item?.terms?.ai}>
                          {t("AI Allowed")}
                        </option>
                      </select>
                    </div>
                  </td>
                )}
                {columnsVisibility.offers && (
                  <td>
                    {item?.offers?.length ? (
                      <button
                        className="btn btn-white btn-sm web-table-offers"
                        type="button"
                        onClick={() => navigateToOffer(item)}
                      >
                        <i style={{ marginRight: "8px" }}>
                          <EditIcon />
                        </i>
                        {item?.offers?.length}{" "}
                        {item?.offers?.length > 1 ? t("Offers") : t("Offer")}
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline-primary btn-sm web-table-offers"
                        type="button"
                        onClick={() => navigateToOffer(item)}
                      >
                        <i className="bi bi-plus-lg me-1"></i>
                        {t("Add Offer")}
                      </button>
                    )}
                  </td>
                )}
                <td className="table-column-ps-0">
                  <div className="dropdowm">
                    <button
                      type="button"
                      className="btn btn-ghost-secondary btn-icon btn-sm rounded-circle "
                      id="teamsDropdown2"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i className="bi-three-dots-vertical"></i>
                    </button>

                    <div
                      className="dropdown-menu dropdown-menu-sm dropdown-menu-end"
                      aria-labelledby="teamsDropdown2"
                    >
                      <button
                        className="dropdown-item d-flex align-items-center"
                        onClick={() => navigateToEditWeb(item?.domain)}
                      >
                        <i style={{ marginLeft: "-2px", marginRight: "8px" }}>
                          <EditIcon />
                        </i>
                        {t("Edit")}
                      </button>
                      {item?.terms?.hidden === false ? (
                        <button
                          className="dropdown-item js-add-to-favorites"
                          onClick={() =>
                            handleShowHideOffer(item?.terms?.uuid, "hide")
                          }
                        >
                          <i className="bi-eye-slash dropdown-item-icon"></i>
                          {t("Hide in Public Offers")}
                        </button>
                      ) : (
                        <button
                          className="dropdown-item js-add-to-favorites"
                          onClick={() =>
                            handleShowHideOffer(item?.terms?.uuid, "show")
                          }
                        >
                          <i className="bi-eye dropdown-item-icon"></i>
                          {t("Show in Public Offers")}
                        </button>
                      )}
                      <div className="dropdown-divider"></div>
                      <button
                        className="d-flex align-items-center dropdown-item text-danger"
                        onClick={() => handleDeleteWeb(item?.terms?.uuid)}
                      >
                        <i style={{ marginLeft: "-2px", marginRight: "8px" }}>
                          <DeleteIcon />
                        </i>{" "}
                        {t("Delete")}
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            ))}
          <tr style={{ visibility: isLoading ? "visible" : "collapse" }}>
            <td colSpan="100%">
              <div
                className="text-center p-4"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  textAlign: "center",
                }}
              >
                <img
                  className="mb-3"
                  src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                  alt=""
                  style={{ width: "10rem" }}
                  data-hs-theme-appearance="dark"
                />
                <img
                  className="mb-3"
                  src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                  alt=""
                  style={{ width: "10rem" }}
                  data-hs-theme-appearance="default"
                />
                <p className="mb-0">
                  {!data || isLoading
                    ? t("Loading data")
                    : t("No data to show")}
                </p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default WebTable;
